import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../App';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const EventCalendar = ({ events, onSelectDate, onSelectEvent }) => {
  const { theme } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and total days
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  let startingDay = firstDayOfMonth.getDay() - 1;
  if (startingDay < 0) startingDay = 6;

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get events for a specific date
  const getEventsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => {
      const eventDate = new Date(event.start_date);
      const eventDateStr = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
      return eventDateStr === dateStr;
    });
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(year, month, day);
    setSelectedDate(clickedDate);
    const dayEvents = getEventsForDate(day);
    if (onSelectDate) {
      onSelectDate(clickedDate, dayEvents);
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return day === selectedDate.getDate() && 
           month === selectedDate.getMonth() && 
           year === selectedDate.getFullYear();
  };

  // Generate calendar grid
  const calendarDays = [];
  
  // Empty cells before first day
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  
  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const cardBg = theme === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const headerBg = theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50';

  return (
    <div className={`rounded-lg border ${cardBg} overflow-hidden`}>
      {/* Calendar Header */}
      <div className={`flex items-center justify-between p-4 ${headerBg}`}>
        <Button variant="ghost" size="icon" onClick={prevMonth}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">
            {MONTHS[month]} {year}
          </h3>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Heute
          </Button>
        </div>
        
        <Button variant="ghost" size="icon" onClick={nextMonth}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
        {WEEKDAYS.map(day => (
          <div 
            key={day} 
            className={`py-2 text-center text-sm font-medium ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return (
              <div 
                key={`empty-${index}`} 
                className={`min-h-[80px] border-b border-r border-gray-100 dark:border-gray-800 ${
                  theme === 'dark' ? 'bg-gray-950' : 'bg-gray-50'
                }`}
              />
            );
          }

          const dayEvents = getEventsForDate(day);
          const hasEvents = dayEvents.length > 0;

          return (
            <div
              key={day}
              onClick={() => handleDateClick(day)}
              className={`
                min-h-[80px] p-1 border-b border-r border-gray-100 dark:border-gray-800
                cursor-pointer transition-colors
                ${isSelected(day) ? 'bg-[#e30613]/10' : ''}
                ${!isSelected(day) && (theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50')}
              `}
            >
              <div className={`
                w-7 h-7 flex items-center justify-center rounded-full text-sm mb-1
                ${isToday(day) ? 'bg-[#e30613] text-white font-bold' : ''}
                ${isSelected(day) && !isToday(day) ? 'bg-gray-200 dark:bg-gray-700' : ''}
              `}>
                {day}
              </div>
              
              {/* Event indicators */}
              <div className="space-y-1">
                {dayEvents.slice(0, 2).map(event => (
                  <div
                    key={event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectEvent && onSelectEvent(event);
                    }}
                    className="text-xs px-1 py-0.5 rounded truncate bg-[#e30613]/20 text-[#e30613] hover:bg-[#e30613]/30"
                    title={event.title}
                  >
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 2 && (
                  <div className="text-xs text-gray-500 px-1">
                    +{dayEvents.length - 2} mehr
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventCalendar;
